import React from "react";
import { Helmet } from "react-helmet";
import { useLocation } from "react-router-dom";

/**
 * SEOMetadata Component
 * Provides comprehensive SEO meta tags for all pages
 * 
 * @param {string} title - Page title
 * @param {string} description - Meta description for SEO
 * @param {string} keywords - Comma-separated keywords
 * @param {string} ogImage - Open Graph image URL (absolute URL)
 * @param {string} canonicalUrl - Canonical URL for the page
 * @param {string} type - Open Graph type (default: "website")
 */
const SEOMetadata = ({
    title,
    description,
    keywords,
    ogImage,
    canonicalUrl,
    type = "website",
}) => {
    const location = useLocation();

    // Default values
    const siteName = "Tuatara ATV";
    const defaultImage = ogImage || `${window.location.origin}/logo192.png`;
    const fullCanonicalUrl = canonicalUrl || `${window.location.origin}${location.pathname}`;
    const fullTitle = title ? title : siteName;

    const organizationSchema = {
        "@context": "https://schema.org",
        "@type": "Organization",
        "name": siteName,
        "url": window.location.origin,
        "logo": `${window.location.origin}/logo192.png`,
        "contactPoint": {
            "@type": "ContactPoint",
            "telephone": "+64-3-347-4956",
            "email": "info@tuataraatv.com",
            "contactType": "customer service"
        },
        "sameAs": [
            "https://www.facebook.com/TuataraATV",
            "https://www.youtube.com/@tuataraatvNZ"
        ]
    };

    return (
        <Helmet>
            {/* Primary Meta Tags */}
            <title>{fullTitle}</title>
            <meta name="description" content={description} />
            {keywords && <meta name="keywords" content={keywords} />}
            <link rel="canonical" href={fullCanonicalUrl} />
            <link rel="icon" href="/favicon.ico" type="image/x-icon" />

            {/* Open Graph / Facebook */}
            <meta property="og:type" content={type} />
            <meta property="og:url" content={fullCanonicalUrl} />
            <meta property="og:title" content={fullTitle} />
            <meta property="og:description" content={description} />
            <meta property="og:image" content={defaultImage} />
            <meta property="og:site_name" content={siteName} />

            {/* Twitter */}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:url" content={fullCanonicalUrl} />
            <meta name="twitter:title" content={fullTitle} />
            <meta name="twitter:description" content={description} />
            <meta name="twitter:image" content={defaultImage} />

            {/* Organization Schema */}
            <script type="application/ld+json">
                {JSON.stringify(organizationSchema)}
            </script>
        </Helmet>
    );
};

export default SEOMetadata;
